























import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import projectsRepository from '@/services/projectsRepository';
import notificationService from '@/services/notificationService';
import projectstatussesRepository from '@/services/projects/projectstatussesRepository';
import Card from '@/components/default/Card.vue';
import Datagrid from '@/components/default/Datagrid.vue';
import dxCustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import customersRepository from '@/services/customers/customersRepository';
import { ProjectLookupContactType } from '@/data/web-api-client';
import AuthenticationService from '@/auth/AuthenticationService';
import { SecurityConfig } from '@/installationConfig';

@Component({
  components: {
    Card,
    Datagrid,
  },
})
export default class ProjectOverviewCard extends Vue {
  @Prop({ required: false })
  public contactId?: string;

  selectedContactId: string = '';
  selectedContactType: ProjectLookupContactType = ProjectLookupContactType.All;
  loading: boolean = false;
  items: any[] = [];
  showFilter: boolean = false;
  pageSizes: number[] = [10, 25, 50, 100];
  stats: IProjectStats | null = null;

  selectedProjectId: string = '';
  statusOptions: any[] = [];

  private authService = new AuthenticationService();

  public async mounted() {
    this.loadStatusOptions();
    const sortValue = this.$route.query.sort;
    let sortType = this.$route.query.sortType;
    if (sortType !== 'asc' && sortType !== 'desc') {
      sortType = 'desc';
    }

    if (sortValue) {
      const datagrid = (this.$refs.grid as any).$refs.projectsTable.instance;
      datagrid.clearSorting();
      datagrid.columnOption(sortValue, 'sortOrder', sortType);
    }
  }

  public refresh() {
    this.selectedProjectId = '';
    this.refreshTable();
  }

  public async loadStatusOptions() {
    try {
      this.loading = true;
      this.statusOptions = (await projectstatussesRepository.get()).data;
    } catch {
      // Do nothing
    } finally {
      this.loading = false;
    }
  }

  public refreshTable() {
    (this.$refs.grid as any).$refs.projectsTable.instance.refresh();
  }

  public add() {
    this.selectedProjectId = '';
    this.$router.push({ path: `/projects/add` });
  }

  public edit(e: any) {
    this.selectedProjectId = e.row.data.id;
  }

  public cellclick(e: any) {
    if (e.column && e.rowType === 'data' && e.column.type !== 'buttons') {
      this.$router.push({ path: `/projects/${e.data.id}` });
    }
  }

  get projecttypes() {
    const catalog = this.$store.getters['catalogs/list']('ProjectTypes');
    return catalog ? catalog.values : [];
  }

  get projectSources() {
    const catalog = this.$store.getters['catalogs/list']('ProjectSources');
    return catalog ? catalog.values : [];
  }

  get users() {
    return this.$store.getters['employees/items'];
  }
  get statusses() {
    const items = this.$store.getters['projects/statusses'];
    if (items) {
      return items;
    }
    return [];
  }

  get datasource() {
    const self = this;
    return new DataSource({
      store: new dxCustomStore({
        key: 'id',
        load: async (loadOptions) => {
          const searchText = (this.$refs.grid as any).$refs.projectsTable.instance.option('searchPanel.text');
          let contact = '';
          contact = self.selectedContactId;

          if (self.contactId && self.contactId !== '') {
            contact = self.contactId;
          }

          const result = await projectsRepository.lookup(loadOptions, searchText, contact, self.selectedContactType);
          return {
            data: result.data.data,
            totalCount: result.data.totalCount,
          };
        },
        byKey: async (val: any) => {
          return projectsRepository.getSingle(val).then((result: any) => {
            return result.data;
          });
        },
        remove: (key: string) => {
          return new Promise(async (resolve, reject) => {
            try {
              await projectsRepository.delete({ id: key });
              notificationService.saved();

              resolve();
            } catch (err) {
              if (err?.response?.status === 403) {
                notificationService.showAxiosError(err);
                resolve();

                return;
              }

              notificationService.failedToSave();
              reject();
            }
          });
        },
      }),
      paginate: true,
    });
  }

  get toolbarOptions() {
    const self = this;

    if (this.contactId && this.contactId !== '') {
      return [];
    }

    return [
      {
        location: 'before',
        widget: 'dxLookup',
        options: {
          dataSource: new DataSource({
            store: new dxCustomStore({
              load: async (loadOptions) => {
                const result = await customersRepository.get(loadOptions);
                return {
                  data: result.data,
                };
              },
              byKey: (val: any) => {
                return val;
              },
            }),
            paginate: true,
          }),
          displayExpr: 'displayName',
          searchEnabled: true,
          showClearButton: true,
          width: 300,
          onValueChanged: (e: any) => {
            if (e && e.value && e.value.id) {
              self.selectedContactId = e.value.id;
              return;
            }
            self.selectedContactId = '';
          },
          placeholder: 'Filter op contact',
        },
      },
      {
        location: 'before',
        widget: 'dxLookup',
        options: {
          dataSource: [
            { value: ProjectLookupContactType.All, text: 'Alle klantsoorten' },
            { value: ProjectLookupContactType.Contact, text: 'Enkel particulieren' },
            { value: ProjectLookupContactType.Company, text: 'Enkel bedrijven' },
          ],
          displayExpr: 'text',
          valueExpr: 'value',
          searchEnabled: true,
          showClearButton: true,
          width: 300,
          onValueChanged: (e: any) => {
            if (e && e.value) {
              self.selectedContactType = e.value;
              return;
            }
            self.selectedContactType = ProjectLookupContactType.All;
          },
          placeholder: 'Contactsoort',
        },
      },
    ];
  }

  get columns() {
    const self = this;
    return [
      {
        dataField: 'projectNumber',
        caption: this.$t('objects.projects.ProjectNumber'),
      },
      {
        dataField: 'customerReference',
        caption: this.$t('objects.projects.CustomerReference'),
      },
      {
        dataField: 'source',
        caption: 'Bron',
        lookup: {
          dataSource: this.projectSources,
        },
      },
      {
        dataField: 'executedPercentage',
        caption: 'Uitgevoerd %',
        dataType: 'number',
        format: 'percent',
      },
      {
        dataField: 'openQuotations',
        caption: 'Onverstuurde offertes',
        dataType: 'number',
      },
      {
        dataField: 'caseNumberRsz',
        caption: this.$t('objects.projects.CaseNumberRsz'),
      },
      {
        dataField: 'name',
        caption: this.$t('objects.projects.Name'),
      },
      {
        dataField: 'type',
        caption: this.$t('objects.projects.ProjectType'),
        lookup: {
          dataSource: this.projecttypes,
          // displayExpr: 'name',
        },
      },
      {
        dataField: 'termsOfPayment',
        caption: this.$t('objects.projects.TermsOfPayment'),
      },
      {
        dataField: 'requestedExecutionDate',
        caption: this.$t('objects.projects.RequestedExecutionDate'),
        dataType: 'date',
        format: 'dd/MM/yyyy',
      },
      {
        dataField: 'status',
        caption: this.$t('objects.projects.Status'),
        lookup: {
          dataSource: self.statusOptions,
          displayExpr: 'name',
          valueExpr: 'value',
        },
      },
      {
        dataField: 'declineReason',
        caption: 'Reden van afwijzing',
      },
      {
        dataField: 'siteLabel',
        caption: 'Werflabel',
        searchEnabled: true,
        visibleIndex: 7,
      },
      {
        dataField: 'userId',
        caption: this.$t('objects.projects.ProjectLeader'),
        lookup: {
          dataSource: this.users,
          displayExpr: 'displayName',
          valueExpr: 'id',
        },
      },
      {
        dataField: 'address.addressline1',
        caption: this.$t('objects.address.Addressline1'),
      },
      {
        dataField: 'address.addressline2',
        caption: this.$t('objects.address.Addressline2'),
      },
      {
        dataField: 'address.zip',
        caption: this.$t('objects.address.Zip'),
      },
      {
        dataField: 'address.city',
        caption: this.$t('objects.address.City'),
      },
      {
        dataField: 'address.country',
        caption: this.$t('objects.address.Country'),
      },
      {
        caption: this.$t('objects.measurements.Plural'),
        columns: [
          {
            dataField: 'stats.measurementsOpen',
            caption: this.$t('objects.measurements.Statusses.0'),
            dataType: 'number',
          },
          {
            dataField: 'stats.measurementsInProgress',
            caption: this.$t('objects.measurements.Statusses.1'),
            dataType: 'number',
          },
          {
            dataField: 'stats.measurementsClosed',
            caption: this.$t('objects.measurements.Statusses.2'),
            dataType: 'number',
          },
          {
            dataField: 'stats.measurementsProcessed',
            caption: this.$t('objects.measurements.Statusses.3'),
            dataType: 'number',
          },
        ],
      },
      this.authService.isNotInRole([SecurityConfig.qualityGuardOneSkinGroup || ''])
        ? {
            caption: 'Statistieken',
            columns: [
              {
                dataField: 'stats.estimatedTurnover',
                caption: 'Verwachte omzet',
                format: {
                  type: 'currency',
                  precision: 2,
                },
                alignment: 'left',
              },
              {
                dataField: 'stats.effectiveTurnover',
                caption: 'Effectieve omzet',
                format: {
                  type: 'currency',
                  precision: 2,
                },
                alignment: 'left',
              },
              {
                dataField: 'stats.estimatedMaterialCost',
                caption: 'Verwachte materiaalkost',
                format: {
                  type: 'currency',
                  precision: 2,
                },
                alignment: 'left',
              },
              {
                dataField: 'stats.effectiveMaterialCost',
                caption: 'Effectieve materiaalkost',
                format: {
                  type: 'currency',
                  precision: 2,
                },
                alignment: 'left',
              },
              {
                dataField: 'stats.estimatedLabourCost',
                caption: 'Verwachte arbeidskost',
                format: {
                  type: 'currency',
                  precision: 2,
                },
                alignment: 'left',
              },
              {
                dataField: 'stats.effectiveLabourCost',
                caption: 'Effectieve arbeidskost',
                format: {
                  type: 'currency',
                  precision: 2,
                },
                alignment: 'left',
              },
              {
                dataField: 'stats.effectiveVariaCost',
                caption: 'Variakost',
                format: {
                  type: 'currency',
                  precision: 2,
                },
                alignment: 'left',
              },
              {
                dataField: 'stats.estimatedTotalCosts',
                caption: 'Verwachte kosten',
                format: {
                  type: 'currency',
                  precision: 2,
                },
                alignment: 'left',
              },
              {
                dataField: 'stats.effectiveTotalCosts',
                caption: 'Effectieve kosten',
                format: {
                  type: 'currency',
                  precision: 2,
                },
                alignment: 'left',
              },
              {
                dataField: 'stats.estimatedProfit',
                caption: 'Verwachte winst',
                format: {
                  type: 'currency',
                  precision: 2,
                },
                alignment: 'left',
              },
              {
                dataField: 'stats.effectiveProfit',
                caption: 'Effectieve winst',
                format: {
                  type: 'currency',
                  precision: 2,
                },
                alignment: 'left',
              },
            ],
          }
        : {},
      {
        type: 'buttons',
        buttons: [
          {
            hint: 'wijzigen',
            icon: 'fas fa-edit',
            onClick: this.edit,
          },
          'delete',
        ],
      },
    ];
  }

  @Watch('selectedContactId')
  onContactChange() {
    this.refreshTable();
  }

  @Watch('selectedContactType')
  onContactTypeChange() {
    this.refreshTable();
  }
}
